import React from 'react';
import styled from 'styled-components';

import { Button, TextButton } from 'src/components/core';
import { DialogHeading } from 'src/components/modals/components/simple-dialog';
import { Shield } from 'src/assets/shield';
import { BrandTintIconBadge } from 'src/components/modals/components/post-signup/brand-tint-icon-badge';
import { formatPhoneHintMask } from 'src/components/modals/phone-verification-nudge/components/otp-entry';

export type InlinePhoneVerificationPromptProps = {
  phoneHint?: string | null;
  onVerify: () => void;
  onChangeNumberClick?: () => void;
  onClose: () => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  purpose?: 'enrollment' | 'redemption';
};

export const InlinePhoneVerificationPrompt = ({
  phoneHint,
  onVerify,
  onChangeNumberClick,
  onClose,
  isSubmitting = false,
  errorMessage = null,
  purpose = 'enrollment',
}: InlinePhoneVerificationPromptProps): JSX.Element => {
  const displayHint = phoneHint ? formatPhoneHintMask(phoneHint.slice(-4)) : null;

  return (
    <Container data-testid='inline-phone-verification-prompt'>
      <StyledHeading fontSize={20}>Verify your phone number</StyledHeading>
      <BrandTintIconBadge $size={64}>
        <Shield width={24} height={26} color='currentColor' />
      </BrandTintIconBadge>
      <BodyText>
        {purpose === 'redemption'
          ? 'A verified phone number is required to redeem loyalty rewards online.'
          : 'A verified phone number is required to join this loyalty program.'}
      </BodyText>
      {displayHint && (
        <PhoneHintText data-testid='inline-phone-verification-hint'>
          We will send a 6-digit code to <strong>{displayHint}</strong>
        </PhoneHintText>
      )}
      {errorMessage && <ErrorMessage data-testid='inline-phone-verification-prompt-error'>{errorMessage}</ErrorMessage>}
      <Button
        onClick={onVerify}
        loading={isSubmitting}
        disabled={isSubmitting}
        mb={16}
        width='264px'
        data-testid='inline-phone-verification-verify-button'
      >
        Send verification code
      </Button>
      {onChangeNumberClick && (
        <SecondaryAction
          type='button'
          onClick={onChangeNumberClick}
          disabled={isSubmitting}
          data-testid='inline-phone-verification-change-number'
        >
          Use a different number
        </SecondaryAction>
      )}
      <StyledTextButton onClick={onClose} disabled={isSubmitting} data-testid='inline-phone-verification-cancel'>
        Not right now
      </StyledTextButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const StyledHeading = styled(DialogHeading)`
  margin-bottom: 20px;
`;

const BodyText = styled.p`
  color: var(--dcm-modal-secondary-text-color, #646d72);
  font-size: 14px;
  line-height: 1.5;
  margin: 16px 0 8px;
  max-width: 360px;
`;

const PhoneHintText = styled.p`
  color: var(--dcm-modal-secondary-text-color, #646d72);
  font-size: 13px;
  margin: 0 0 20px;
`;

const ErrorMessage = styled.p`
  color: var(--dcm-phone-verification-nudge-error-color, ${({ theme }) => theme.colors.red?.[55] ?? '#E53935'});
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 16px;
`;

const SecondaryAction = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--dcm-modal-link-color, ${({ theme }) => theme.colors.primaryBrand ?? '#146443'});
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
  padding: 4px;
  text-decoration: underline;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledTextButton = styled(TextButton)`
  font-weight: 600;
`;
